import { useState, useEffect } from "react";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import { Plus, Shield, X, Save, CheckSquare, Edit, Trash2 } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

const MODULES = [
  { id: "dashboard", label: "Dashboard" },
  { id: "jobs", label: "Manage Jobs" },
  { id: "candidates", label: "Candidates" },
  { id: "top_companies", label: "Top Companies" },
  { id: "testimonials", label: "Testimonials" },
  { id: "manage_partners", label: "Manage Partners" },
  { id: "employers", label: "Employers" },
  { id: "manage_videos", label: "Manage Videos" },
  { id: "employees", label: "Employees" },
  { id: "roles", label: "Roles & Permissions" },
  { id: "settings", label: "Settings" }
];

const ACTIONS = ["view", "create", "edit", "delete"];

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 🌟 Naye Edit States
  const [editingRoleId, setEditingRoleId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    permissions: {}, 
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await api.get("/roles", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (moduleId, action) => {
    setFormData((prev) => {
      const modulePerms = prev.permissions[moduleId] || [];
      let newPerms;

      if (action === "all") {
        const isAllSelected = modulePerms.length === ACTIONS.length;
        newPerms = isAllSelected ? [] : [...ACTIONS];
      } else {
        if (modulePerms.includes(action)) {
          newPerms = modulePerms.filter((a) => a !== action);
        } else {
          newPerms = [...modulePerms, action];
        }
      }

      const newPermissions = { ...prev.permissions };
      
      if (newPerms.length === 0) {
        delete newPermissions[moduleId];
      } else {
        newPermissions[moduleId] = newPerms;
      }

      return { ...prev, permissions: newPermissions };
    });
  };

  // 🌟 CREATE ya UPDATE form handle karega
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Object.keys(formData.permissions).length === 0) {
      return toast.error("Please assign at least one permission!");
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("admin_token");
      
      if (editingRoleId) {
        // UPDATE API CALL
        await api.put(`/roles/${editingRoleId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Role updated successfully!");
      } else {
        // CREATE API CALL
        await api.post("/roles", formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("New Role created successfully!");
      }
      
      closeModal();
      fetchRoles(); 
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save role.");
    } finally {
      setSubmitting(false);
    }
  };

  // 🌟 EDIT Button Click Handler
  const handleEdit = (role) => {
    setFormData({
      name: role.name,
      permissions: getParsedPermissions(role.permissions)
    });
    setEditingRoleId(role.id);
    setIsModalOpen(true);
  };

  // 🌟 DELETE Button Click Handler
  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete the role "${name}"?`)) {
      try {
        const token = localStorage.getItem("admin_token");
        await api.delete(`/roles/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Role deleted successfully!");
        fetchRoles();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete role.");
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRoleId(null);
    setFormData({ name: "", permissions: {} });
  };

  const getParsedPermissions = (permData) => {
    if (!permData) return {};
    if (typeof permData === 'object' && !Array.isArray(permData)) return permData;
    try { return JSON.parse(permData); } catch { return {}; }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <PageTitle title="Roles & Permissions" subtitle="Create, update and manage master roles." />
        <button 
          onClick={() => {
            setEditingRoleId(null);
            setFormData({ name: "", permissions: {} });
            setIsModalOpen(true);
          }}
          className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-2.5 rounded-lg font-semibold transition-colors cursor-pointer"
        >
          <Plus size={20} /> Create New Role
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider font-bold border-b border-gray-100">
                <th className="py-4 px-6 w-1/5">Role Name</th>
                <th className="py-4 px-6">Assigned Permissions Details</th>
                <th className="py-4 px-6 text-center w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="3" className="py-8 text-center text-gray-500">Loading roles...</td></tr>
              ) : roles.length === 0 ? (
                <tr><td colSpan="3" className="py-8 text-center text-gray-500">No roles created yet.</td></tr>
              ) : (
                roles.map((role) => {
                  const perms = getParsedPermissions(role.permissions);
                  return (
                    <tr key={role.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="py-5 px-6 font-bold text-gray-900 flex items-center gap-2 align-top">
                        <Shield size={18} className="text-yellow-500" /> {role.name}
                      </td>
                      <td className="py-5 px-6">
                        {Object.keys(perms).length === 0 ? (
                            <span className="text-gray-400 text-sm italic">No permissions</span>
                        ) : (
                            <div className="flex flex-wrap gap-3">
                            {Object.entries(perms).map(([mod, actions]) => (
                                <div key={mod} className="bg-white border border-gray-200 shadow-sm rounded-lg px-3 py-1.5 flex flex-col">
                                    <span className="text-xs font-extrabold text-gray-700 uppercase tracking-wide">
                                        {mod.replace('_', ' ')}
                                    </span>
                                    <span className="text-[10px] font-bold text-yellow-600 mt-0.5">
                                        {actions.length === 4 ? 'FULL ACCESS' : actions.join(', ').toUpperCase()}
                                    </span>
                                </div>
                            ))}
                            </div>
                        )}
                      </td>
                      
                      {/* 🌟 NAYA ACTION COLUMN */}
                      <td className="py-5 px-6 align-top">
                        <div className="flex justify-center items-center gap-2">
                            <button 
                                onClick={() => handleEdit(role)} 
                                className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl transition-colors cursor-pointer" 
                                title="Edit Role"
                            >
                                <Edit size={16} />
                            </button>
                            <button 
                                onClick={() => handleDelete(role.id, role.name)} 
                                className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-colors cursor-pointer" 
                                title="Delete Role"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
            
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
              <h3 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                <CheckSquare size={22} className="text-yellow-500"/> 
                {editingRoleId ? "Edit Role Permissions" : "Define Role Permissions"}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
              <div className="p-6 overflow-y-auto">
                <div className="mb-6 w-full max-w-md">
                  <label className="block text-sm font-bold text-gray-700 mb-2">Role Name *</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="e.g. Senior Employer, Editor"
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500 font-medium" 
                  />
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-100/80 text-gray-600 text-xs uppercase tracking-wider font-extrabold border-b border-gray-200">
                                <th className="py-3 px-4 w-1/3">Module Name</th>
                                <th className="py-3 px-4 text-center">All</th>
                                <th className="py-3 px-4 text-center">View</th>
                                <th className="py-3 px-4 text-center">Create</th>
                                <th className="py-3 px-4 text-center">Edit</th>
                                <th className="py-3 px-4 text-center">Delete</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                            {MODULES.map((mod) => {
                                const currentPerms = formData.permissions[mod.id] || [];
                                const isAll = currentPerms.length === ACTIONS.length;

                                return (
                                <tr key={mod.id} className="hover:bg-yellow-50/30 transition-colors">
                                    <td className="py-3 px-4 font-bold text-gray-800">{mod.label}</td>
                                    
                                    <td className="py-3 px-4 text-center border-r border-gray-100/50">
                                        <input 
                                            type="checkbox" 
                                            checked={isAll}
                                            onChange={() => handlePermissionChange(mod.id, 'all')}
                                            className="w-4 h-4 text-yellow-500 rounded border-gray-300 focus:ring-yellow-500 cursor-pointer"
                                        />
                                    </td>

                                    {ACTIONS.map(action => (
                                        <td key={action} className="py-3 px-4 text-center">
                                            <input 
                                                type="checkbox" 
                                                checked={currentPerms.includes(action)}
                                                onChange={() => handlePermissionChange(mod.id, action)}
                                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                            />
                                        </td>
                                    ))}
                                </tr>
                                )
                            })}
                        </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 mt-auto shrink-0">
                <button type="button" onClick={closeModal} className="px-6 py-2.5 text-sm font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2.5 rounded-xl font-bold transition-colors cursor-pointer disabled:opacity-50 shadow-md">
                  <Save size={18} /> {submitting ? "Saving..." : (editingRoleId ? "Update Role" : "Save Role")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}